import React, { useEffect, useMemo, useRef, useState } from "react";
import "./portfolio.css";
import { projectData } from "./projectData";

const Portfolio = () => {
  const [activeCategory, setActiveCategory] = useState("All");
  const [query, setQuery] = useState("");
  const [selectedProject, setSelectedProject] = useState(null);
  const [isOpen, setIsOpen] = useState(false);

  const sectionRef = useRef(null);

  const categories = useMemo(() => {
    const cats = new Set(projectData.map((p) => p.category).filter(Boolean));
    return ["All", ...Array.from(cats)];
  }, []);

  const filteredProjects = useMemo(() => {
    const q = query.trim().toLowerCase();

    return projectData.filter((p) => {
      const matchesCategory = activeCategory === "All" ? true : p.category === activeCategory;

      const hay =
        `${p.title || ""} ${p.category || ""} ${p.description || ""} ${(p.stack || []).join(" ")}`
          .toLowerCase();

      const matchesQuery = q ? hay.includes(q) : true;

      return matchesCategory && matchesQuery;
    });
  }, [activeCategory, query]);

  const openProject = (project) => {
    setSelectedProject(project);
    setIsOpen(true);
  };

  const closeProject = () => {
    setIsOpen(false);
    setSelectedProject(null);
  };

  // ESC close + scroll lock
  useEffect(() => {
    if (!isOpen) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") closeProject();
    };

    document.addEventListener("keydown", onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen]);

  // reveal animation on scroll
  useEffect(() => {
    const root = sectionRef.current;
    if (!root) return;

    const cards = root.querySelectorAll(".pfCard");

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("is-visible");
        });
      },
      { threshold: 0.12 }
    );

    cards.forEach((c) => io.observe(c));
    return () => io.disconnect();
  }, [activeCategory, query]);

  return (
    <section className="portfolio section" id="portfolio" ref={sectionRef}>
      {/* ✅ CENTERED + PADDED WRAPPER (fixes desktop edge-hugging) */}
      <div className="portfolio__wrapper container">
        <div className="pfHeader">
           <div className="pfTitleBlock">
    <h2 className="section__title pfCenteredTitle">Portfolio</h2>
    <span className="section__subtitle pfCenteredSubtitle">
      Selected work & real builds
    </span>
  </div>

          <div className="pfSearchWrap">
            <i className="uil uil-search pfSearchIcon" aria-hidden="true"></i>
            <input
              className="pfSearch"
              type="text"
              placeholder='Search projects (e.g. "dashboard", "school", "payment")'
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            {query && (
              <button
                className="pfClear"
                type="button"
                onClick={() => setQuery("")}
                aria-label="Clear search"
              >
                <i className="uil uil-times"></i>
              </button>
            )}
          </div>
        </div>

        <div className="pfControls">
          <div className="pfChips" role="tablist" aria-label="Project categories">
            {categories.map((cat) => (
              <button
                key={cat}
                type="button"
                role="tab"
                aria-selected={activeCategory === cat}
                className={activeCategory === cat ? "pfChip pfChip--active" : "pfChip"}
                onClick={() => setActiveCategory(cat)}
              >
                {cat}
                {activeCategory === cat && <span className="pfChipGlow" aria-hidden="true" />}
              </button>
            ))}
          </div>

          <div className="pfMeta">
            <span className="pfCount">
              <strong>{filteredProjects.length}</strong>{" "}
              project{filteredProjects.length === 1 ? "" : "s"}
            </span>
          </div>
        </div>

        <div className="pfGrid">
          {filteredProjects.map((p, idx) => (
            <button
              type="button"
              key={p.id ?? `${p.title}-${idx}`}
              className="pfCard"
              onClick={() => openProject(p)}
            >
              <div className="pfThumb">
                <img src={p.image} alt={p.title} loading="lazy" />
                <div className="pfThumbOverlay">
                  <span className="pfPill">{p.category || "Project"}</span>
                  <span className="pfOpen">
                    View details <i className="uil uil-arrow-right"></i>
                  </span>
                </div>
              </div>

              <div className="pfBody">
                <h3 className="pfTitle">{p.title}</h3>
                <p className="pfDesc">{p.description || "Click to view project details."}</p>

                {Array.isArray(p.stack) && p.stack.length > 0 && (
                  <div className="pfTags">
                    {p.stack.slice(0, 4).map((t) => (
                      <span className="pfTag" key={t}>
                        {t}
                      </span>
                    ))}
                    {p.stack.length > 4 && (
                      <span className="pfTag pfTag--more">+{p.stack.length - 4}</span>
                    )}
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>

        {filteredProjects.length === 0 && (
          <div className="pfEmpty">
            <i className="uil uil-folder-question"></i>
            <p>No projects match your search/filter. Try a different keyword.</p>
          </div>
        )}
      </div>

      {/* MODAL (outside wrapper so it’s full-screen) */}
      {isOpen && selectedProject && (
        <div
          className="pfModalOverlay"
          onClick={(e) =>
            e.target.classList.contains("pfModalOverlay") && closeProject()
          }
        >
          <div className="pfModal">
            <button className="pfModalClose" type="button" onClick={closeProject} aria-label="Close">
              <i className="uil uil-times"></i>
            </button>

            <div className="pfModalHero">
              <img src={selectedProject.image} alt={selectedProject.title} />
              <div className="pfModalHeroShade" aria-hidden="true" />
              <div className="pfModalHeroInfo">
                <span className="pfPill">{selectedProject.category || "Project"}</span>
                <h3 className="pfModalTitle">{selectedProject.title}</h3>

                {(selectedProject.year || selectedProject.role) && (
                  <div className="pfModalMeta">
                    {selectedProject.year && (
                      <span className="pfMetaPill">
                        <i className="uil uil-calendar-alt"></i> {selectedProject.year}
                      </span>
                    )}
                    {selectedProject.role && (
                      <span className="pfMetaPill">
                        <i className="uil uil-user"></i> {selectedProject.role}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="pfModalBody">
              <div className="pfModalText">
                <h4>Overview</h4>
                <p>{selectedProject.description}</p>

                {Array.isArray(selectedProject.highlights) && selectedProject.highlights.length > 0 && (
                  <>
                    <h4>Highlights</h4>
                    <ul className="pfModalList">
                      {selectedProject.highlights.map((h, i) => (
                        <li key={i}>
                          <i className="uil uil-check-circle"></i>
                          <span>{h}</span>
                        </li>
                      ))}
                    </ul>
                  </>
                )}

                {Array.isArray(selectedProject.stack) && selectedProject.stack.length > 0 && (
                  <>
                    <h4>Tech</h4>
                    <div className="pfModalTags">
                      {selectedProject.stack.map((t) => (
                        <span className="pfTag" key={t}>
                          {t}
                        </span>
                      ))}
                    </div>
                  </>
                )}
              </div>

              <div className="pfModalActions">
                {selectedProject.demo && (
                  <a className="pfBtn pfBtn--primary" href={selectedProject.demo} target="_blank" rel="noreferrer">
                    Live Demo <i className="uil uil-external-link-alt"></i>
                  </a>
                )}

                {selectedProject.repo && (
                  <a className="pfBtn pfBtn--ghost" href={selectedProject.repo} target="_blank" rel="noreferrer">
                    GitHub <i className="uil uil-github"></i>
                  </a>
                )}

                {!selectedProject.demo && !selectedProject.repo && (
                  <div className="pfNote">
                    <i className="uil uil-info-circle"></i>
                    <span>
                      Add <code>demo</code> or <code>repo</code> fields in <code>projectData</code> to show links.
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div className="pfModalFooter">
              <span className="pfHint">
                Tip: press <kbd>ESC</kbd> to close
              </span>
              <button className="pfBtn pfBtn--ghost" type="button" onClick={closeProject}>
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Portfolio;
